import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Ideas", "Recipes", "Lifestyle", "Faith"];

export default function BrandApply() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    brand_name: "",
    product_name: "",
    category: "",
    description: "",
    website: "",
    submitted_by_email: "",
    photo_path: "",
  });

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // The upload endpoint requires auth. Brand applicants are usually not
      // Willoah users — for MVP, ask them to also sign in with Google if they
      // want to attach a photo. If not signed in, they get a friendly error.
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, photo_path: data.path }));
      toast("Photo uploaded.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed. You may need to sign in first.");
      setPreview(null);
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo_path) return toast("Please upload a photo of the product.");
    if (!form.category) return toast("Choose a category.");
    setBusy(true);
    try {
      await api.post("/brand-products", form);
      toast("Application received. The site owner reviews these personally.");
      navigate("/try-and-critique");
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not submit.");
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 pt-12 pb-24">
      <span className="accent-label text-[#B79256]">apply · try &amp; critique</span>
      <h1 className="font-serif-display text-5xl text-[#2A2823] mt-3">Get honestly reviewed</h1>
      <p className="text-[#2A2823]/70 mt-4 leading-relaxed">
        Willoah charges a flat fee for a product to enter the Try &amp; Critique pool. That's it — the fee buys a fair shot at being tested by real users. It does not buy placement in The Grove, positive reviews, or any special treatment.
      </p>

      <div className="mt-6 rounded-2xl border border-[#B79256]/30 bg-white/70 p-5 text-sm text-[#2A2823]/80 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-[#4E5A3D] flex-shrink-0 mt-0.5" strokeWidth={1.75}/>
        <div>
          <b>Rules of the pool:</b> reviews land labeled "Gifted to me", brands never see them before they post, we don't remove negative reviews, and nothing here is ever boosted in the main feed.
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="apply-form">
        <div>
          <Label className="accent-label text-[#B79256]">Product photo</Label>
          <div
            onClick={() => fileRef.current?.click()}
            data-testid="apply-dropzone"
            className="mt-3 cursor-pointer rounded-3xl border-2 border-dashed border-[#B79256]/50 bg-white/60 aspect-[4/3] flex items-center justify-center overflow-hidden hover:bg-[#B79256]/8 transition-colors"
          >
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover"/>
            ) : (
              <div className="text-center text-[#2A2823]/60">
                <ImagePlus className="w-8 h-8 mx-auto mb-3 text-[#B79256]" strokeWidth={1.5}/>
                <p className="font-serif-display text-xl text-[#2A2823]">{uploading ? "Uploading…" : "Upload a photo"}</p>
                <p className="text-xs mt-2 accent-label text-[#2A2823]/50">jpg or png · up to 8mb</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden"/>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="accent-label text-[#B79256]">Brand name</Label>
            <Input data-testid="apply-brand-name" required value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"/>
          </div>
          <div>
            <Label className="accent-label text-[#B79256]">Product name</Label>
            <Input data-testid="apply-product-name" required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"/>
          </div>
          <div>
            <Label className="accent-label text-[#B79256]">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="apply-category" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"><SelectValue placeholder="Choose one"/></SelectTrigger>
              <SelectContent className="bg-[#FBF8F1] border-[#B79256]/40">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="accent-label text-[#B79256]">Website (optional)</Label>
            <Input data-testid="apply-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"/>
          </div>
        </div>

        <div>
          <Label className="accent-label text-[#B79256]">Contact email</Label>
          <Input data-testid="apply-email" type="email" required value={form.submitted_by_email} onChange={(e) => setForm({ ...form, submitted_by_email: e.target.value })} placeholder="hello@yourbrand.com" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"/>
        </div>

        <div>
          <Label className="accent-label text-[#B79256]">Short description</Label>
          <Textarea data-testid="apply-description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} placeholder="What the product is, who it's for, and any allergens/notes reviewers should know." className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"/>
        </div>

        <Button data-testid="apply-submit" type="submit" disabled={busy || uploading} className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 px-7 text-base shadow-none">
          {busy ? "Sending…" : "Submit application"}
        </Button>
      </form>
    </main>
  );
}
